import { Input } from 'antd';
import React, { useState, useEffect, useRef, useCallback } from 'react';

import type { InputRef } from 'antd';

interface NumberInputWithMaskProps {
  value?: number;
  onChange?: (value: number) => void;
  disabled?: boolean;
  dataTestId?: string;
  suffix?: string;
}

export const NumberInputWithMask: React.FC<NumberInputWithMaskProps> = ({
  value = 0,
  onChange,
  disabled,
  dataTestId = '',
  suffix = '',
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const inputRef = useRef<InputRef>(null);
  const lastValueRef = useRef('');

  const INTEGER_PART_MAX_LENGTH = 11;
  const DECIMAL_PART_MAX_LENGTH = 2;

  const formatWithSpaces = useCallback((numStr: string): string => {
    if (!numStr) return '';

    const [integerPart, decimalPart] = numStr.split('.');
    const formattedInteger = integerPart?.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    return decimalPart !== undefined
      ? `${formattedInteger}.${decimalPart}`
      : formattedInteger;
  }, []);

  const sanitizeValue = useCallback((input: string): string => {
    if (!input) return '';

    let cleaned = input?.replace(/,/g, '.')?.replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = `${parts[0]}.${parts.slice(1).join('')}`;
    }

    const [integerPart = '', decimalPart] = cleaned.split('.');
    const limitedInteger = integerPart.slice(0, INTEGER_PART_MAX_LENGTH);
    const limitedDecimal =
      decimalPart !== undefined
        ? decimalPart.slice(0, DECIMAL_PART_MAX_LENGTH)
        : undefined;

    return limitedDecimal !== undefined
      ? `${limitedInteger}.${limitedDecimal}`
      : limitedInteger;
  }, []);

  const adjustCursorPosition = useCallback(
    (
      oldValue: string,
      newValue: string,
      oldCursor: number,
      inputValue: string
    ): number => {
      if (oldCursor >= oldValue.length) {
        return newValue.length;
      }

      const isSeparatorInput =
        inputValue[oldCursor - 1] === '.' || inputValue[oldCursor - 1] === ',';
      if (isSeparatorInput) {
        const pointIndex = newValue.indexOf('.');
        return pointIndex !== -1 ? pointIndex + 1 : newValue.length;
      }

      const oldRaw = oldValue?.replace(/\s/g, '');
      const newRaw = newValue?.replace(/\s/g, '');
      const isDeletion = newRaw.length < oldRaw.length;

      let cleanOldPos = 0;
      let spacesBeforeOld = 0;
      for (let i = 0; i < oldCursor && i < oldValue.length; i++) {
        if (oldValue[i] !== ' ') cleanOldPos++;
        else spacesBeforeOld++;
      }

      let cleanNewPos = 0;
      let newPos = 0;
      let spacesBeforeNew = 0;
      for (let j = 0; j < newValue.length && cleanNewPos < cleanOldPos; j++) {
        if (newValue[j] !== ' ') cleanNewPos++;
        else spacesBeforeNew++;
        newPos++;
      }

      if (isDeletion && cleanOldPos > 0) {
        const spacesLost = spacesBeforeOld - spacesBeforeNew;
        if (
          spacesLost > 0 &&
          oldCursor > 0 &&
          oldValue[oldCursor - 1] !== ' '
        ) {
          newPos = newPos;
        } else {
          newPos = Math.max(newPos - spacesLost, 0);
          if (oldCursor > 0 && oldValue[oldCursor - 1] === ' ') {
            newPos = Math.max(newPos - 1, 0);
          }
        }
      } else if (!isDeletion && newRaw.length > oldRaw.length) {
        newPos++;
        if (newPos < newValue.length && newValue[newPos] === ' ') {
          newPos++;
        }
      }

      return Math.min(Math.max(newPos, 0), newValue.length);
    },
    []
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const cursorPos = input.selectionStart || 0;
      const oldValue = displayValue;
      const inputValue = input.value;

      const sanitized = sanitizeValue(inputValue);
      const formattedWithSpaces = sanitized ? formatWithSpaces(sanitized) : '';

      setDisplayValue(formattedWithSpaces);
      lastValueRef.current = formattedWithSpaces;

      if (onChange) {
        const numericValue = parseFloat(sanitized);
        const value = Number.isNaN(numericValue) ? 0 : numericValue;
        if (sanitized === '.') {
          setDisplayValue('0');
        }
        onChange(value);
      }

      requestAnimationFrame(() => {
        if (inputRef.current?.input) {
          const newCursorPos = adjustCursorPosition(
            oldValue,
            formattedWithSpaces,
            cursorPos,
            inputValue
          );
          inputRef.current.input.setSelectionRange(newCursorPos, newCursorPos);
        }
      });
    },
    [
      onChange,
      formatWithSpaces,
      sanitizeValue,
      displayValue,
      adjustCursorPosition,
    ]
  );

  useEffect(() => {
    const sanitized = sanitizeValue(value.toString());
    const formattedWithSpaces = sanitized ? formatWithSpaces(sanitized) : '';
    if (formattedWithSpaces !== lastValueRef.current) {
      setDisplayValue(formattedWithSpaces);
      lastValueRef.current = formattedWithSpaces;
    }
  }, [value, formatWithSpaces, sanitizeValue]);

  return (
    <Input
      ref={inputRef}
      type="text"
      disabled={disabled}
      value={displayValue}
      onChange={handleChange}
      data-testid={dataTestId}
      maxLength={
        INTEGER_PART_MAX_LENGTH +
        DECIMAL_PART_MAX_LENGTH +
        1 +
        Math.floor(INTEGER_PART_MAX_LENGTH / 3)
      }
      suffix={suffix}
    />
  );
};
